import React from 'react';
import { Grid } from '@mui/material';

import { AutocompleteField, BoolField } from '../../CommonComponents/Form';
import { SectionDivider } from '../../CommonComponents';

import { fetchAllGroups, selectMyGroups } from '../../../redux/actions/groups';
import { useDispatch, useSelector } from '../../../redux/hooks';
import { fetchAllUsers, selectAllUsers } from '../../../redux/actions/users';

type SettingsType = {
    publishDirectly: boolean,
    publishPublicly: boolean,
    showTenantName: boolean,
    groups: number[],
    creatingUserId: number,
    status: string
}

type PropsType = {
    settings: SettingsType,
    onChangeSettings: (e: Partial<SettingsType>) => void
}

export default function LogtradeForm({
    settings,
    onChangeSettings
}: PropsType) {
    const dispatch = useDispatch();
    const groups = useSelector(selectMyGroups());
    const fetchGroups = React.useCallback(() => {
        dispatch(fetchAllGroups());
    }, [dispatch]);
    const users = useSelector(selectAllUsers());
    const fetchUsers = React.useCallback(() => {
        dispatch(fetchAllUsers());
    }, [dispatch]);
    const [disableEdit, setDisableEdit] = React.useState(false);

    React.useEffect(() => {
        fetchGroups();
        fetchUsers();
    }, [fetchGroups, fetchUsers]);

    React.useEffect(() => {
        if (settings.status !== 'COMPLETE') {
            setDisableEdit(true);
        } else {
            setDisableEdit(false);
        }
    }, [settings]);

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <SectionDivider label="Grupper och användare" marginTop={2} marginBottom={1} />
                    </Grid>
                    <Grid item xs={12}>
                        <AutocompleteField<{ id: number, name: string, ownerTenantName?: string }>
                            label="Publicera i grupp"
                            helperText="Välj grupper eller publicera publikt"
                            optionIdKey="id"
                            fetchOptions={fetchGroups}
                            options={groups.filter(
                                (o: { isOwner: boolean; canPublish: boolean; }) => (o.isOwner || o.canPublish)
                            )}
                            getOptionName={(o) => o.name}
                            getOptionDescription={(o) => o.ownerTenantName || ''}
                            onChange={(g) => onChangeSettings({
                                groups: g.map((group: any) => group.id),
                                publishPublicly: g.length > 0 ? false : undefined
                            })}
                            fetchOnlyOnce
                            displayOption={groups.filter((g) => settings.groups?.includes(g.id)) || []}
                            multiple
                            required={!settings.publishPublicly}
                            disabled={disableEdit}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <AutocompleteField<{ id: number, firstName: string, lastName: string, title?: string }>
                            label="Publicera som användare"
                            helperText="Välj användare att publicera uppdrag som"
                            optionIdKey="id"
                            fetchOptions={fetchUsers}
                            options={users}
                            getOptionName={(o) => `${o.firstName} ${o.lastName}`}
                            getOptionDescription={(o) => o.title || ''}
                            onChange={(user) => {
                                if (user?.id) {
                                    onChangeSettings({
                                        creatingUserId: user.id
                                    });
                                }
                            }}
                            fetchOnlyOnce
                            disableClearable
                            displayOption={users.find((u) => settings.creatingUserId === u.id) || null}
                            required
                            disabled={disableEdit}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12} lg={6}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <SectionDivider label="Publicering" marginTop={2} marginBottom={1} />
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <BoolField
                            name="publishPublicly"
                            label="Publicera publikt"
                            helperText="Välj om uppdrag ska publiceras publikt eller endast i valda grupper."
                            value={settings.publishPublicly || false}
                            onChange={() => onChangeSettings({
                                publishPublicly: !settings.publishPublicly,
                                groups: !settings.publishPublicly ? [] : settings.groups

                            })}
                            disabled={disableEdit}
                        />
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <BoolField
                            name="publishDirectly"
                            label="Publicera direkt"
                            helperText="Välj om uppdrag som skapas i Logtrade ska
                            publiceras direkt eller om de ska sparas som utkast."
                            value={settings.publishDirectly || false}
                            onChange={onChangeSettings}
                            disabled={disableEdit}
                        />
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <BoolField
                            name="showTenantName"
                            label="Visa företagsnamn"
                            helperText="Välj om ni vill visa ert företagsnamn på de uppdrag som publiceras."
                            value={settings.showTenantName || false}
                            onChange={onChangeSettings}
                            disabled={disableEdit}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
}
